import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import { useNavigate } from "react-router-dom";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { dateTime, money, surfaceSx } from "../center/ownerCenterUi";
import OwnerClientProjectLinkDialog from "./OwnerClientProjectLinkDialog";

const USER_STATUS_LABELS = { active: "Activo", pending: "Pendiente", disabled: "Deshabilitado" };
const PROJECT_STATUS_LABELS = { active: "Activo", paused: "Pausado", cancelled: "Cancelado" };
const READINESS_LABELS = { starting: "Inicial", building: "En construcción", launchable: "Publicable", operational: "Operativo" };
const MEMBER_ROLE_LABELS = { owner: "Owner", admin: "Administrador", manager: "Responsable", editor: "Editor", operator: "Operador", viewer: "Lectura" };
const USER_ROLE_LABELS = { superadmin: "Superadmin", admin: "Administrador", client: "Cliente" };
const BILLING_STATUS_LABELS = {
  trial: "Prueba",
  pending: "Pendiente",
  active: "Activo",
  past_due: "Pago vencido",
  paused: "Pausado",
  cancelled: "Cancelado",
  exempt: "Exento",
};

function billingTone(status) {
  if (["active", "trial", "exempt"].includes(status)) return "success";
  if (["past_due", "paused", "pending"].includes(status)) return "warning";
  return "default";
}

function ProjectCard({ project, detected = false, saving = false, onLink, onUnlink }) {
  const navigate = useNavigate();
  return (
    <Box sx={{ py: 1.2 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography sx={{ fontWeight: 1000, fontSize: ".86rem" }}>{project.name}</Typography>
            <Chip size="small" label={PROJECT_STATUS_LABELS[project.status] || "Sin estado"} sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} />
            {detected ? <Chip size="small" variant="outlined" label="Detectado, no confirmado" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : <Chip size="small" color="success" variant="outlined" label="Vinculado" sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} />}
            {project.billing?.status ? <Chip size="small" color={billingTone(project.billing.status)} label={`Billing: ${BILLING_STATUS_LABELS[project.billing.status] || project.billing.status}`} sx={{ height: 20, fontSize: ".62rem", fontWeight: 900 }} /> : null}
          </Stack>
          <Typography sx={{ mt: .35, color: "text.secondary", fontSize: ".72rem" }}>
            {project.slug || "sin slug"} · {project.plan || "sin plan"}{project.businessProfile ? ` · ${project.businessProfile}` : ""}
            {project.membership?.role ? ` · cuenta: ${MEMBER_ROLE_LABELS[project.membership.role] || project.membership.role}` : ""}
          </Typography>
          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap sx={{ mt: .75 }}>
            {project.billing?.monthlyAmount > 0 ? <Stack direction="row" spacing={.4} alignItems="center"><PaymentsRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} /><Typography sx={{ fontSize: ".72rem", fontWeight: 900 }}>{money(project.billing.monthlyAmount, project.billing.currency)} / mes</Typography></Stack> : <Typography sx={{ color: "text.secondary", fontSize: ".7rem" }}>Sin monto mensual de billing</Typography>}
            {project.billing?.nextPaymentDate ? <Typography sx={{ color: "text.secondary", fontSize: ".7rem" }}>Próximo billing {dateTime(project.billing.nextPaymentDate)}</Typography> : null}
            {project.readiness ? <Typography sx={{ fontSize: ".7rem", fontWeight: 850 }}>Readiness {project.readiness.score}% · {READINESS_LABELS[project.readiness.level] || project.readiness.level}</Typography> : null}
          </Stack>
          {project.deployment?.publicUrl ? (
            <Link href={project.deployment.publicUrl} target="_blank" rel="noreferrer" underline="hover" sx={{ display: "inline-flex", mt: .7, alignItems: "center", gap: .4, fontSize: ".72rem", fontWeight: 850, overflowWrap: "anywhere" }}>
              <LanguageRoundedIcon sx={{ fontSize: 15 }} />{project.deployment.publicUrl}
            </Link>
          ) : <Typography sx={{ mt: .65, color: "text.secondary", fontSize: ".7rem" }}>Sin URL pública detectada.</Typography>}
        </Box>
        <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap alignItems="flex-start">
          {detected ? (
            <Button size="small" variant="contained" startIcon={<AddLinkRoundedIcon />} disabled={saving} onClick={onLink} sx={{ textTransform: "none", fontWeight: 900 }}>Vincular proyecto</Button>
          ) : (
            <Button size="small" color="error" startIcon={<LinkOffRoundedIcon />} disabled={saving} onClick={onUnlink} sx={{ textTransform: "none", fontWeight: 850 }}>Desvincular</Button>
          )}
          {project.access?.canOpenPanel ? <Button size="small" variant="outlined" onClick={() => navigate(project.links.projectHome)} sx={{ textTransform: "none", fontWeight: 850 }}>Abrir proyecto</Button> : null}
          {project.access?.canOpenSuper360 ? <Button size="small" endIcon={<OpenInNewRoundedIcon />} onClick={() => navigate(project.links.super360)} sx={{ textTransform: "none", fontWeight: 850 }}>360°</Button> : null}
          {!project.access?.canOpenPanel && !project.access?.canOpenSuper360 && project.deployment?.publicUrl ? <Button size="small" component="a" href={project.deployment.publicUrl} target="_blank" rel="noreferrer" endIcon={<OpenInNewRoundedIcon />} sx={{ textTransform: "none", fontWeight: 850 }}>Abrir web</Button> : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export default function OwnerClientWebProfitPanel({
  client,
  context,
  loading = false,
  error = "",
  onLinkUser,
  onContextUpdated,
  onRetry,
}) {
  const [projectDialogOpen, setProjectDialogOpen] = React.useState(false);
  const [savingProjectId, setSavingProjectId] = React.useState("");
  const [actionError, setActionError] = React.useState("");
  const user = context?.user;
  const projects = context?.projects || [];
  const detectedProjects = context?.detectedProjects || [];

  if (!context && loading) {
    return <Box sx={{ ...surfaceSx, p: 2, minHeight: 110, display: "grid", placeItems: "center" }}><Stack spacing={1} alignItems="center"><CircularProgress size={24} /><Typography sx={{ color: "text.secondary", fontSize: ".78rem" }}>Cargando cuenta y proyectos WebProfit…</Typography></Stack></Box>;
  }
  if (!context && error) {
    return <Box sx={{ ...surfaceSx, p: 2 }}><Alert severity="error" action={<Button size="small" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert></Box>;
  }

  const setProjectLink = async (projectId, linked) => {
    setSavingProjectId(projectId);
    setActionError("");
    try {
      const response = await ownerApi.linkClientWebProfitProject(client.id || client._id, projectId, linked);
      onContextUpdated?.(response?.context || null);
    } catch (err) {
      setActionError(formatApiError(err, linked ? "No pude vincular el proyecto." : "No pude desvincular el proyecto."));
    } finally {
      setSavingProjectId("");
    }
  };

  return (
    <>
      <Box sx={{ ...surfaceSx, p: { xs: 1.6, sm: 2 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Typography sx={{ fontWeight: 1000 }}>WebProfit</Typography>
            <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".75rem" }}>Cuenta global y proyectos relacionados, sin copiar User ni Project dentro del CRM Owner.</Typography>
          </Box>
          {loading ? <CircularProgress size={20} /> : null}
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 1 }} action={<Button size="small" onClick={onRetry} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}
        {actionError ? <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert> : null}

        <Box sx={{ mt: 1.25, p: 1.15, borderRadius: 2.3, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.03)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 900 }}>CUENTA WEBPROFIT</Typography>
              {user ? (
                <>
                  <Stack direction="row" spacing={.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .3 }}>
                    <Typography sx={{ fontWeight: 950, fontSize: ".84rem" }}>{user.name}</Typography>
                    <Chip size="small" label={USER_STATUS_LABELS[user.status] || user.status} sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
                  </Stack>
                  <Typography sx={{ mt: .2, color: "text.secondary", fontSize: ".72rem", overflowWrap: "anywhere" }}>{user.email} · {USER_ROLE_LABELS[user.globalRole] || user.globalRole}</Typography>
                  <Typography sx={{ mt: .15, color: "text.secondary", fontSize: ".68rem" }}>Último acceso: {user.lastLoginAt ? dateTime(user.lastLoginAt) : "sin registro"}</Typography>
                </>
              ) : <Typography sx={{ mt: .35, color: "text.secondary", fontSize: ".78rem" }}>Sin cuenta vinculada. La persona puede seguir funcionando normalmente como CRM Owner.</Typography>}
            </Box>
            <Button variant={user ? "outlined" : "contained"} startIcon={<LinkRoundedIcon />} onClick={onLinkUser} sx={{ textTransform: "none", fontWeight: 900 }}>
              {user ? "Cambiar cuenta" : "Vincular cuenta"}
            </Button>
          </Stack>
        </Box>

        {!user && context?.suggestedUser ? (
          <Alert severity="info" sx={{ mt: 1 }} action={<Button size="small" onClick={onLinkUser} sx={{ textTransform: "none", fontWeight: 900 }}>Revisar</Button>}>
            Coincide el email con <strong>{context.suggestedUser.name}</strong> · {context.suggestedUser.email}. Es sólo una sugerencia: no se vinculó automáticamente.
          </Alert>
        ) : null}

        <Divider sx={{ my: 1.4 }} />

        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} alignItems={{ sm: "center" }}>
          <Box>
            <Typography sx={{ fontWeight: 950, fontSize: ".88rem" }}>Proyectos WebProfit vinculados</Typography>
            <Typography sx={{ mt: .16, color: "text.secondary", fontSize: ".7rem" }}>Relación comercial explícita. Los datos se leen en vivo desde Project.</Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<AddLinkRoundedIcon />} onClick={() => setProjectDialogOpen(true)} sx={{ textTransform: "none", fontWeight: 900 }}>Buscar Project</Button>
        </Stack>
        {projects.length ? <Stack divider={<Divider flexItem />}>{projects.map((project) => <ProjectCard key={project.id} project={project} saving={savingProjectId === project.id} onUnlink={() => setProjectLink(project.id, false)} />)}</Stack> : <Typography sx={{ py: 1.4, color: "text.secondary", fontSize: ".8rem" }}>Sin Project WebProfit confirmado para esta relación.</Typography>}

        {detectedProjects.length ? (
          <>
            <Divider sx={{ my: 1.35 }} />
            <Typography sx={{ fontWeight: 950, fontSize: ".88rem" }}>Detectados por la cuenta WebProfit</Typography>
            <Typography sx={{ mt: .16, color: "text.secondary", fontSize: ".7rem" }}>La membresía de la cuenta sirve para descubrirlos, pero no confirma que comercialmente pertenezcan a este cliente.</Typography>
            <Stack divider={<Divider flexItem />}>{detectedProjects.map((project) => <ProjectCard key={project.id} project={project} detected saving={savingProjectId === project.id} onLink={() => setProjectLink(project.id, true)} />)}</Stack>
          </>
        ) : null}
      </Box>

      <OwnerClientProjectLinkDialog
        open={projectDialogOpen}
        client={client}
        linkedProjectIds={projects.map((project) => project.id)}
        onClose={() => setProjectDialogOpen(false)}
        onLinked={(nextContext) => onContextUpdated?.(nextContext)}
      />
    </>
  );
}
